import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Trash2, 
  Loader2, 
  AlertTriangle,
  Database,
  Users,
  Package,
  FileText,
  Layers,
  RefreshCw,
  Download,
  Shield,
  Lock,
  KeyRound,
  Sparkles,
  CheckCircle2,
  Tags,
  ArrowRight,
  Server,
  FileSpreadsheet,
  ShieldAlert,
  ScanLine,
} from 'lucide-react';
import api, { scanOrderFormulas } from '../lib/api';
import { authenticateGate } from '../lib/accessGate';
import { SystemHealthPanel } from './SystemHealthPanel';
import { CustomerImportTool } from './data-management/CustomerImportTool';
import { ProductImportTool } from './data-management/ProductImportTool';
import { PendingAddressReview } from './data-management/PendingAddressReview';
import { PdfFooterSettings } from './data-management/PdfFooterSettings';
import { DeploymentPanel } from './data-management/DeploymentPanel';
import { DataUtilities, FeatureToggles } from './data-management/DataUtilities';
import { DangerZone, EntityBrowser } from './data-management/DangerZone';

// Developer access password is held only in backend/.env. The token
// returned by /api/access-gate/auth gates dev-mode access here too.
const DEV_ACCESS_KEY = 'dm_access_granted';

export default function DataManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Password protection state
  const [accessGranted, setAccessGranted] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(true);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteType, setDeleteType] = useState('');
  const [filters, setFilters] = useState({
    customerFilter: 'all',
    dispatchFilter: 'all',
  });

  // Entity-specific data
  const [customers, setCustomers] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [cobrandings, setCobrandings] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedDispatches, setSelectedDispatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  
  // Seed data state
  const [seeding, setSeeding] = useState(false);
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [scanningFormulas, setScanningFormulas] = useState(false);
  const [seedProgress, setSeedProgress] = useState({ step: '', completed: [] });
  
  // Clear all dialog
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);

  // Check if already authenticated for this session
  useEffect(() => {
    const sessionAccess = sessionStorage.getItem(DEV_ACCESS_KEY);
    if (sessionAccess === 'true') {
      setAccessGranted(true);
      setPasswordDialogOpen(false);
    }
  }, []);

  const handlePasswordSubmit = async () => {
    setPasswordError('');
    try {
      await authenticateGate(passwordInput);
      sessionStorage.setItem(DEV_ACCESS_KEY, 'true');
      setAccessGranted(true);
      setPasswordDialogOpen(false);
      toast.success('Developer access granted');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setPasswordError('Incorrect password');
      } else if (status === 503) {
        setPasswordError('Access gate not configured');
      } else {
        setPasswordError('Network error. Try again.');
      }
      setPasswordInput('');
    }
  };

  const handlePasswordCancel = () => {
    setPasswordDialogOpen(false);
    toast.error('Access denied - Restricted section');
    navigate(-1);
  };

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [customersRes, dispatchesRes, batchesRes, productsRes, cobrandingsRes] = await Promise.allSettled([
        api.get('/customers'),
        api.get('/dispatches'),
        api.get('/batches'),
        api.get('/products'),
        api.get('/cobranding'),
      ]);
      
      const customersData = customersRes.status === 'fulfilled' ? customersRes.value.data || [] : [];
      const dispatchesData = dispatchesRes.status === 'fulfilled' ? dispatchesRes.value.data || [] : [];
      const batchesData = batchesRes.status === 'fulfilled' ? batchesRes.value.data || [] : [];
      const productsData = productsRes.status === 'fulfilled' ? productsRes.value.data || [] : [];
      const cobrandingsData = cobrandingsRes.status === 'fulfilled' ? cobrandingsRes.value.data || [] : [];
      
      const customersNoAddress = customersData.filter(c => !c.delivery_address_line1);
      const customersNoOrders = customersData.filter(c => !dispatchesData.some(d => d.customer_id === c.id));
      const draftDispatches = dispatchesData.filter(d => d.status === 'draft');
      const orphanedDispatches = dispatchesData.filter(d => !customersData.some(c => c.id === d.customer_id));
      
      setStats({
        totalCustomers: customersData.length,
        customersNoAddress: customersNoAddress.length,
        customersNoOrders: customersNoOrders.length,
        totalDispatches: dispatchesData.length,
        draftDispatches: draftDispatches.length,
        orphanedDispatches: orphanedDispatches.length,
        totalBatches: batchesData.length,
        totalProducts: productsData.length,
        totalCobrandings: cobrandingsData.length,
      });
      
      setCustomers(customersData);
      setDispatches(dispatchesData);
      setBatches(batchesData);
      setProducts(productsData);
      setCobrandings(cobrandingsData);
    } catch {
      toast.error('Failed to load data statistics');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (type) => {
    setDeleteType(type);
    setConfirmText('');
    setConfirmDialogOpen(true);
  };

  const handleMassDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleting(true);
    try {
      let deleted = 0;
      let failed = 0;
      
      if (deleteType === 'customers' && selectedCustomers.length > 0) {
        for (const id of selectedCustomers) {
          try { await api.delete(`/customers/${id}?confirm=true`); deleted++; } catch { failed++; }
        }
        setSelectedCustomers([]);
      } else if (deleteType === 'dispatches' && selectedDispatches.length > 0) {
        for (const id of selectedDispatches) {
          try { await api.post(`/dispatches/${id}/delete`, { reason: 'other', notes: 'Mass delete via admin' }); deleted++; } catch { failed++; }
        }
        setSelectedDispatches([]);
      } else if (deleteType === 'batches' && selectedBatches.length > 0) {
        for (const id of selectedBatches) {
          try { await api.delete(`/batches/${id}`); deleted++; } catch { failed++; }
        }
        setSelectedBatches([]);
      }
      
      toast.success(`Deleted ${deleted} items${failed > 0 ? `, ${failed} failed` : ''}`);
      setConfirmDialogOpen(false);
      loadStats();
    } catch {
      toast.error('Delete operation failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleClearAllData = async () => {
    if (confirmText !== 'DELETE ALL') {
      toast.error('Please type DELETE ALL to confirm');
      return;
    }

    setDeleting(true);
    const results = { dispatches: 0, batches: 0, customers: 0, products: 0, cobrandings: 0 };
    
    try {
      for (const d of dispatches) {
        try { await api.post(`/dispatches/${d.id}/delete`, { reason: 'other', notes: 'Clear all data' }); results.dispatches++; } catch {}
      }
      for (const b of batches) {
        try { await api.delete(`/batches/${b.id}`); results.batches++; } catch {}
      }
      for (const c of customers) {
        try { await api.delete(`/customers/${c.id}?confirm=true`); results.customers++; } catch {}
      }
      for (const p of products) {
        try { await api.delete(`/products/${p.id}?confirm=true`); results.products++; } catch {}
      }
      for (const cb of cobrandings) {
        try { await api.delete(`/cobranding/${cb.id}`); results.cobrandings++; } catch {}
      }
      
      toast.success(
        `Cleared: ${results.dispatches} dispatches, ${results.batches} batches, ` +
        `${results.customers} customers, ${results.products} products, ${results.cobrandings} co-brandings`
      );
      setClearAllDialogOpen(false);
      setConfirmText('');
      loadStats();
    } catch {
      toast.error('Clear operation failed');
    } finally {
      setDeleting(false);
    }
  };

  // Seed sample data
  const handleSeedData = async () => {
    setSeeding(true);
    setSeedProgress({ step: 'Starting...', completed: [] });
    
    try {
      const sampleProducts = [
        { sku: 'CB-001', name: 'Cashel Blue', units_per_case: 6 },
        { sku: 'CB-002', name: 'Crozier Blue', units_per_case: 6 },
        { sku: 'SS-003', name: "Shepherd's Store", units_per_case: 4 },
        { sku: 'CT-004', name: 'Cashel Tome', units_per_case: 4 },
        { sku: 'RC-005', name: 'Reserve Cheddar', units_per_case: 8 },
      ];
      
      setSeedProgress({ step: 'Creating products...', completed: [] });
      let productsCreated = 0;
      for (const product of sampleProducts) {
        try { await api.post('/products', product); productsCreated++; } catch {}
      }
      setSeedProgress(prev => ({ ...prev, completed: [...prev.completed, `${productsCreated} products created`] }));
      
      const sampleCustomers = [
        { name: 'Fallon & Byrne', delivery_address_line1: '11-17 Exchequer Street', delivery_city: 'Dublin 2', delivery_country: 'Ireland', contact_name: 'Purchasing Dept', contact_email: 'orders@fallonandbyrne.com', contact_phone: '+353 1 472 1000' },
        { name: 'Sheridans Cheesemongers', delivery_address_line1: '11 South Anne Street', delivery_city: 'Dublin 2', delivery_country: 'Ireland', contact_name: 'Kevin Sheridan', contact_email: 'dublin@sheridanscheesemongers.com', contact_phone: '+353 1 679 3143' },
        { name: 'Supervalu Distribution', delivery_address_line1: 'Tramore Road', delivery_city: 'Cork', delivery_country: 'Ireland', contact_name: 'Goods In', contact_email: 'warehouse@supervalu.ie', contact_phone: '+353 21 480 0500' },
        { name: 'Dunnes Stores Central', delivery_address_line1: '46-50 South Great Georges Street', delivery_city: 'Dublin 2', delivery_country: 'Ireland', contact_name: 'Dairy Buyer', contact_email: 'dairy@dunnesstores.com', contact_phone: '+353 1 475 1111' },
        { name: 'Avoca Handweavers', delivery_address_line1: 'Kilmacanogue', delivery_city: 'Co. Wicklow', delivery_country: 'Ireland', contact_name: 'Food Hall Manager', contact_email: 'foodhall@avoca.com', contact_phone: '+353 1 286 7466' },
      ];
      
      setSeedProgress(prev => ({ ...prev, step: 'Creating customers...' }));
      let customersCreated = 0;
      for (const customer of sampleCustomers) {
        try { await api.post('/customers', customer); customersCreated++; } catch {}
      }
      setSeedProgress(prev => ({ ...prev, completed: [...prev.completed, `${customersCreated} customers created`] }));
      
      const sampleCoBrandings = [
        { name: 'Fallon & Byrne Label', description: 'Custom label for F&B' },
        { name: 'Supervalu Own Brand', description: 'Supervalu private label' },
        { name: 'Export Label - UK', description: 'UK market labeling' },
        { name: 'Export Label - EU', description: 'EU export labeling' },
      ];
      
      setSeedProgress(prev => ({ ...prev, step: 'Creating co-brandings...' }));
      let cobrandingsCreated = 0;
      for (const cb of sampleCoBrandings) {
        try { await api.post('/cobranding', cb); cobrandingsCreated++; } catch {}
      }
      setSeedProgress(prev => ({ ...prev, completed: [...prev.completed, `${cobrandingsCreated} co-brandings created`] }));
      
      setSeedProgress(prev => ({ ...prev, step: 'Updating settings...' }));
      try {
        const settingsRes = await api.get('/settings');
        const currentSettings = settingsRes.data;
        await api.put('/settings', {
          ...currentSettings,
          transport_options: ['CMT', 'Dawn Meats', 'AOR Logistics', 'Customer Collection', 'DPD/Courier', 'Pallet Track', 'Other'],
          product_formats: ['Full Wheel', 'Half Wheel', 'Quarter', 'Wedge 200g', 'Wedge 150g', 'Sliced Pack', 'Gift Box']
        });
        setSeedProgress(prev => ({ ...prev, completed: [...prev.completed, 'Transport options & formats configured'] }));
      } catch {}
      
      setSeedProgress(prev => ({ ...prev, step: 'Complete!' }));
      toast.success('Sample data seeded successfully!');
      setTimeout(() => { loadStats(); setSeedDialogOpen(false); setSeedProgress({ step: '', completed: [] }); }, 1500);
    } catch {
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  // Check access: admin role OR developer password
  if (!accessGranted && user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold">Access Denied</h2>
            <p className="text-muted-foreground mt-2">This page is only accessible to administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6" /> Data Management
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Advanced tools for managing and cleaning up data</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setSeedDialogOpen(true)} data-testid="btn-seed-data">
            <Sparkles className="w-4 h-4 mr-2" /> Seed Sample Data
          </Button>
          {user?.role === 'super_admin' && (
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm('Re-seed the Public Sandbox tenant (demo@keystone.cloudvoro.com)? This wipes and rebuilds the demo data but never touches your tenant.')) return;
                const t = toast.loading('Seeding public sandbox… (up to 60s)');
                try {
                  const r = await api.post('/admin/seed-public-sandbox');
                  toast.dismiss(t);
                  toast.success(`Sandbox seeded, login: ${r.data.demo_email}`);
                } catch (e) {
                  toast.dismiss(t);
                  toast.error(e.response?.data?.detail || 'Seed failed');
                }
              }}
              data-testid="btn-seed-public-sandbox"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Seed Public Sandbox
            </Button>
          )}
          <Button variant="destructive" onClick={() => setClearAllDialogOpen(true)} data-testid="btn-clear-all">
            <Trash2 className="w-4 h-4 mr-2" /> Clear ALL Data
          </Button>
          <Button variant="outline" onClick={loadStats}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Clear ALL Data Dialog */}
      <Dialog open={clearAllDialogOpen} onOpenChange={setClearAllDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Clear ALL Data
            </DialogTitle>
            <DialogDescription>This will permanently delete ALL data from the system.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800 mb-2">This will delete:</p>
              <ul className="text-sm space-y-1 list-disc list-inside text-red-700">
                <li>All {stats.totalCustomers} customers</li>
                <li>All {stats.totalDispatches} dispatches</li>
                <li>All {stats.totalBatches} batches</li>
                <li>All {stats.totalProducts} products</li>
                <li>All {stats.totalCobrandings} co-brandings</li>
              </ul>
            </div>
            <Label>Type "DELETE ALL" to confirm:</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE ALL" className="font-mono" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearAllDialogOpen(false)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleClearAllData} disabled={deleting || confirmText !== 'DELETE ALL'}>
              {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" /> Delete Everything</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed Data Dialog */}
      <Dialog open={seedDialogOpen} onOpenChange={setSeedDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" /> Seed Sample Data
            </DialogTitle>
            <DialogDescription>Create sample products, customers, and co-brandings for testing.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {seedProgress.step && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  {seeding && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span className="font-medium">{seedProgress.step}</span>
                </div>
                {seedProgress.completed.map((msg, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle2 className="w-4 h-4" /> {msg}
                  </div>
                ))}
              </div>
            )}
            {!seedProgress.step && (
              <p className="text-sm text-muted-foreground">
                This will create 5 sample products, 5 sample customers, and 4 co-brandings. Existing data will not be affected.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeedDialogOpen(false)} disabled={seeding}>Cancel</Button>
            <Button onClick={handleSeedData} disabled={seeding}>
              {seeding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Seeding...</> : <><Sparkles className="w-4 h-4 mr-2" /> Start Seeding</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card><CardContent className="pt-4"><div className="flex items-center gap-3"><Users className="w-8 h-8 text-blue-500" /><div><p className="text-2xl font-bold">{stats.totalCustomers}</p><p className="text-sm text-muted-foreground">Customers</p></div></div><div className="mt-2 text-xs space-y-1"><p className="text-amber-600">{stats.customersNoAddress} without address</p><p className="text-gray-500">{stats.customersNoOrders} without orders</p></div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="flex items-center gap-3"><FileText className="w-8 h-8 text-green-500" /><div><p className="text-2xl font-bold">{stats.totalDispatches}</p><p className="text-sm text-muted-foreground">Dispatches</p></div></div><div className="mt-2 text-xs space-y-1"><p className="text-amber-600">{stats.draftDispatches} drafts</p><p className="text-red-600">{stats.orphanedDispatches} orphaned</p></div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="flex items-center gap-3"><Layers className="w-8 h-8 text-purple-500" /><div><p className="text-2xl font-bold">{stats.totalBatches}</p><p className="text-sm text-muted-foreground">Batches</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="flex items-center gap-3"><Package className="w-8 h-8 text-orange-500" /><div><p className="text-2xl font-bold">{stats.totalProducts || 0}</p><p className="text-sm text-muted-foreground">Products</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="flex items-center gap-3"><Tags className="w-8 h-8 text-pink-500" /><div><p className="text-2xl font-bold">{stats.totalCobrandings || 0}</p><p className="text-sm text-muted-foreground">Co-brandings</p></div></div></CardContent></Card>
      </div>

      {/* Sales Order Purge Card */}
      <Card className="border-red-200 bg-red-50/30 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/data-management/sales-order-purge')} data-testid="sales-order-purge-card">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-red-800">Sales Order Purge</h3>
                <p className="text-xs text-red-600/70">Search, soft-delete errant sales orders and all associated data with full audit trail</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-red-400" />
          </div>
        </CardContent>
      </Card>

      {/* Scan Formulas Card */}
      <Card className="border-emerald-200 bg-emerald-50/30" data-testid="scan-formulas-card">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <ScanLine className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-emerald-800">Scan Past Orders for Formulas</h3>
                <p className="text-xs text-emerald-600/70">Derive case/weight/unit formulas from past sales orders. Never overwrites existing formulas.</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              disabled={scanningFormulas}
              data-testid="btn-scan-formulas"
              onClick={async () => {
                setScanningFormulas(true);
                try {
                  const { data } = await scanOrderFormulas();
                  toast.success(`Scan complete: ${data.formulas_applied} formula(s) applied, ${data.skipped_existing} skipped (already set), ${data.no_mapping_found} had no mapping`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Scan failed');
                } finally {
                  setScanningFormulas(false);
                }
              }}
            >
              {scanningFormulas ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Run Scan'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System Health Monitor */}
      <SystemHealthPanel />

      {/* Database Host Card */}
      <Card className="border-indigo-200 bg-indigo-50/30 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/data-management/database-host')} data-testid="db-host-card">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Server className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-indigo-800">Database Host</h3>
                <p className="text-xs text-indigo-600/70">Switch between local MongoDB and Atlas, manage migrations</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-indigo-400" />
          </div>
        </CardContent>
      </Card>

      {/* API Settings Card */}
      <Card className="border-teal-200 bg-teal-50/30 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/data-management/api-settings')} data-testid="api-settings-card">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-teal-800">API Settings</h3>
                <p className="text-xs text-teal-600/70">Manage API keys for secure programmatic access to your data</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-teal-400" />
          </div>
        </CardContent>
      </Card>

      {/* Data Export/Import Section */}
      <Card className="border-emerald-200 bg-emerald-50/30">
        <CardHeader>
          <CardTitle className="text-emerald-700 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" /> Data Export & Re-Import
          </CardTitle>
          <CardDescription>Export data as JSON for backup, or re-import from exported files</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2"><Download className="w-4 h-4 text-emerald-600" /> Customer Export/Import</h4>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={async () => {
                  try {
                    const response = await api.get('/customers/export-json', { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url; link.download = `customers_export_${new Date().toISOString().split('T')[0]}.json`;
                    link.click(); window.URL.revokeObjectURL(url);
                    toast.success('Customer data exported');
                  } catch { toast.error('Export failed'); }
                }} data-testid="btn-export-customers">
                  <Download className="w-4 h-4 mr-1" /> Export JSON
                </Button>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2"><Download className="w-4 h-4 text-emerald-600" /> Product Export/Import</h4>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={async () => {
                  try {
                    const response = await api.get('/products/export-json', { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url; link.download = `products_export_${new Date().toISOString().split('T')[0]}.json`;
                    link.click(); window.URL.revokeObjectURL(url);
                    toast.success('Product data exported');
                  } catch { toast.error('Export failed'); }
                }} data-testid="btn-export-products">
                  <Download className="w-4 h-4 mr-1" /> Export JSON
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Extracted Components */}
      <CustomerImportTool onImportComplete={loadStats} />
      <ProductImportTool onImportComplete={loadStats} />
      <PendingAddressReview />
      <PdfFooterSettings />
      <DataUtilities />
      <FeatureToggles />
      <DeploymentPanel />

      {/* Danger Zone */}
      <DangerZone stats={stats} onCleanupComplete={loadStats} />

      {/* Entity Browser */}
      <EntityBrowser
        customers={customers}
        dispatches={dispatches}
        batches={batches}
        selectedCustomers={selectedCustomers}
        setSelectedCustomers={setSelectedCustomers}
        selectedDispatches={selectedDispatches}
        setSelectedDispatches={setSelectedDispatches}
        selectedBatches={selectedBatches}
        setSelectedBatches={setSelectedBatches}
        filters={filters}
        setFilters={setFilters}
        stats={stats}
        onDelete={openDeleteDialog}
      />

      {/* Mass Delete Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              {deleteType === 'customers'
                ? `This will permanently delete ${selectedCustomers.length} customer(s).`
                : deleteType === 'dispatches'
                  ? `This will permanently delete ${selectedDispatches.length} dispatch(es).`
                  : `This will permanently delete ${selectedBatches.length} batch(es).`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Type "DELETE" to confirm:</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="mt-2 font-mono" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleMassDelete} disabled={deleting || confirmText !== 'DELETE'}>
              {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" /> Confirm Delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Protection Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" /> Developer Access Required
            </DialogTitle>
            <DialogDescription>This section requires developer access. Please enter the access password.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div>
              <Label htmlFor="password">Access Password</Label>
              <Input
                id="password"
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                placeholder="Enter password"
                className="mt-2"
                autoFocus
                data-testid="dev-access-password"
              />
            </div>
            <p className="text-xs text-muted-foreground">Hint: <span className="font-mono bg-muted px-1.5 py-0.5 rounded">Fov !!</span></p>
            {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handlePasswordCancel}>Cancel</Button>
            <Button onClick={handlePasswordSubmit} data-testid="dev-access-submit">
              <KeyRound className="w-4 h-4 mr-2" /> Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
